<template>
  <div class="editor" :class="{ minimized: focused, saving: saving }" :style="`height: ${height}px`">
    <EditorHintBar
      ref="topBar"
      :loading="loading"
      :saving="saving"
      :hints="hints"
      @dragStart="dragStart"/>

    <EditorContent
      :loading="loading"
      :hints="hints"
      :error="error"/>

    <EditorActionBar
      :loading="loading"
      :made-changes="madeChanges"
      :saving="saving"
      :errors="errors"
      :url-dev="urlDev"
      @save="saveHints"/>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import { Events } from '../events'
import { RepositoryFactory } from '../data/RepositoryFactory'
import { debug } from '../util/log'
import EditorActionBar from './editor/EditorActionBar'
import EditorContent from './editor/EditorContent'
import EditorHintBar from './editor/EditorHintBar'

const OHMRepository = RepositoryFactory.get('ohm')

export default {
  name: 'Editor',
  data: () => ({
    height: 300,

    beforeMinimizeSize: null,
    madeChanges: false,
    saving: false,
    dragging: null,
    errors: [],

    dragCallback: null,

    // Modals
    showErrors: false,
    showHelp: false,

    // Misc
    workers: [],
  }),
  components: { EditorHintBar, EditorContent, EditorActionBar },
  props: {
    urlDev: {
      type: String
    },
    hints: {
      default: null
    },
    loading: {
      default: false,
      type: Boolean
    },
    error: {
      type: String
    }
  },
  provide: function () {
    return {
      dragStart: this.dragStart
    }
  },
  watch: {
    selectedHint() {
      this.updatePositions()
    },
    selectedArticle: {
      immediate: true,
      handler(val) {
        this.$store.dispatch('editor/loadArticleData', val)

        if (val && this.hints) {
          this.validateHintRules()
        }
      }
    },
    hints: {
      immediate: true,
      handler(val) {
        if (val && this.selectedHint === null) {
          debug(val[Object.keys(val)[0]])
          this.$store.commit('hint/setSelectedHint', val[Object.keys(val)[0]])
        }
      }
    },
    focused(val) {
      // Keep track of the grid size
      if (val) {
        this.beforeMinimizeSize = document.getElementById('home').style.gridTemplateRows
        document.getElementById('home').style.gridTemplateRows = '1fr auto'
      } else {
        if (this.beforeMinimizeSize) {
          document.getElementById('home').style.gridTemplateRows = this.beforeMinimizeSize
        }
      }
    }
  },
  created() {
    // TODO: first get the site information. Remove this site-getting logic from hintFunctions, and expect a valid id_site to be provided
    document.addEventListener('keydown', (e) => {
      if (e.code === 'KeyS' && e.ctrlKey) {
        e.preventDefault()
        this.saveHints()
        return false
      } else if (e.code === 'KeyE' && e.ctrlKey && !e.shiftKey) {
        e.preventDefault()
        this.reclassify()
        return false
      } else if (e.code === 'KeyE' && e.ctrlKey && e.shiftKey) {
        e.preventDefault()
        //this.reclassifyAll() // TODO: Uncomment once reclassifying doesn't remove STT transcription text
        return false
      }
    })

    this.$store.dispatch('hint/getHintFunctionMap')
    .then(() => {})

    this.$store.dispatch('hint/getHintFunctions')
    .then(() => {
      // Begin registering relevant listeners

      // FIXME: belongs in EditorContent
      Events.$on('addHintStatement', payload => {
        const args = []
        if (payload.hintFunction.args) {
          payload.hintFunction.args.forEach(() => {
            args.push({
              arg: ''
            })
          })
        }

        this.madeChanges = true
        this.$store.commit('hint/addStatementToSelectedHint', {
          args: args,
          function_id: payload.hintFunction.id,
          active: true,
          python_name: null
        })

        this.updatePositions()

        // TODO: clean up this mess
        // If the hint we added has any arguments, focus the first one
        if (args) {
          // Element hasn't actually been inserted into the DOM yet, so give it a little time
          this.$nextTick(() => {
            const tbody = document.getElementById('hint-statements')

            // The statement we've just added is the last child
            const lastChild = tbody.childNodes[tbody.childNodes.length - 1]

            for (let n = 0; n < lastChild.childNodes.length; n++) {
              if (lastChild.childNodes[n].className === 'function-args') {
                for (let a = 0; a < lastChild.childNodes[n].childNodes.length; a++) {
                  // Focus the first argument we find
                  if (lastChild.childNodes[n].childNodes[a].className === 'function-arg') {
                    for (let i = 0; i < lastChild.childNodes[n].childNodes[a].childNodes.length; i++) {
                      if (lastChild.childNodes[n].childNodes[a].childNodes[i].tagName === 'INPUT') {
                        lastChild.childNodes[n].childNodes[a].childNodes[i].focus()
                        break
                      } else if (lastChild.childNodes[n].childNodes[a].childNodes[i].className === 'autocomplete-container') {
                        lastChild.childNodes[n].childNodes[a].childNodes[i].childNodes[0].focus()
                        break
                      }
                    }
                    break
                  }
                }
                break
              }
            }
          })

        }
      })

      // FIXME: belongs in EditorContent
      Events.$on('deleteHintStatement', payload => {
        if (this.$store.commit('hint/deleteStatementFromSelectedHint', payload.position)) {
          this.madeChanges = true
        }
        this.updatePositions()
      })

      Events.$on('madeChanges', () => {
        this.madeChanges = true
      })
    }).catch(() => {
      Events.$emit('addNotification', {
        type: 'error',
        msg: 'Failed to load hint functions',
        timeout: 3
      })
    })
  },
  methods: {
    validateHintRules() {
      // Attempts to validate each hint URL rule against the currently selected article
      if (!this.selectedArticle || !this.hints) {
        return
      }

      // Cancel already-pending workers
      this.workers.forEach((workerTimeout) => {
        clearTimeout(workerTimeout)
        debug('Cancel timeout')
      })
      this.workers = []

      // Check for any invalid url rules
      for (let i = 0; i < Object.keys(this.hints).length; i++) {
        const hint = this.hints[Object.keys(this.hints)[i]]

        const worker = new Worker('/js/worker.js')
        worker.postMessage([hint.url_rule, this.selectedArticle.url])

        const timeout = setTimeout(() => {
          Events.$emit('addNotification', {
            type: 'error',
            msg: 'Found invalid URL filter: ' + hint.url_rule,
            timeout: 8
          })

          this.hints[Object.keys(this.hints)[i]].invalid_url_rule = true
        }, 10000)

        worker.onmessage = (e) => {
          clearTimeout(timeout)
          this.hints[Object.keys(this.hints)[i]].invalid_url_rule = false
          if (e.data === 1) {
            // required for reactivity's sake (otherwise it won't update on initial page load)
            this.$set(this.hints[Object.keys(this.hints)[i]], 'url_matches', true)
          } else {
            this.$set(this.hints[Object.keys(this.hints)[i]], 'url_matches', false)
          }
        }

        this.workers.push(timeout)
      }
    },
    dragStart(e) {
      if (this.focused || e.button !== 0) {
        return
      }

      this.draggingCallback = event => this.drag(event, e)

      // mouseup/mousemove doesn't get called when cursor is above iframe, so we show a div that lies above the iframe with a higher z-index to solve this
      const dragProtect = document.getElementById('drag-protect')
      if (dragProtect) {
        dragProtect.style.display = 'block'
      }

      document.addEventListener('mousemove', this.draggingCallback)
      document.addEventListener('mouseup', this.dragStop)
    },
    drag(e) {
      const clientHeight = document.documentElement.clientHeight
      const editorHeight = Math.max(100, clientHeight - e.pageY)

      this.height = Math.min(editorHeight, clientHeight - 28) // Navbar is 28px
    },
    dragStop() {
      const dragProtect = document.getElementById('drag-protect')
      if (dragProtect) {
        dragProtect.style.display = 'none'
      }

      document.removeEventListener('mouseup', this.dragStop)
      document.removeEventListener('mousemove', this.draggingCallback)
      this.draggingCallback = null
    },
    splitArgs(argString) {
      // Rewrite of the splitArgs method from the API
      let args = []

      if (argString != null) {
        let outerQ = ''
        let singleQ = 0
        let doubleQ = 0

        let curArg = ''

        for (let i = 0; i < argString.length; i++) {
          let char = argString.charAt(i)
          curArg += char

          if (char === '"' || char === "'") {
            if (i > 0 && argString.charAt(i - 1) === "\\") {
              continue
            }
            if (outerQ === '') {
              outerQ = char
            }
            if (char === '"') {
              doubleQ++
            } else {
              singleQ++
            }

            if ((outerQ === '"' && (doubleQ % 2 === 0)) || (outerQ === "'" && (singleQ % 2 === 0))) {
              if (curArg.trim().length) {
                args.push({ 'arg': curArg.trim() })
              }
              curArg = ''
              outerQ = ''
              singleQ = 0
              doubleQ = 0

              if ((i + 1) <= argString.length) {
                if (argString.charAt(i + 1) === ',') {
                  i++
                }
              }
            }
          } else if (outerQ === '') {
            if (i === argString.length - 1) {
              if (curArg.trim().length) {
                args.push({ 'arg': curArg.trim() })
              }
              break
            }

            if (argString.charAt(i + 1) === ',') {
              if (curArg.trim().length) {
                args.push({ 'arg': curArg.trim() })
              }
              curArg = ''
              outerQ = ''
              singleQ = 0
              doubleQ = 0

              i++
            }
          }
        }
      }

      return args
    },
    saveHints() {
      if (this.madeChanges && !this.saving && !this.loading) {
        this.saving = true

        // Before sending we need to:
        // - Remove any currently highlighted errors, so that things that have been fixed don't remain highlighted
        // - Split up any `list` arguments into multiple args
        // - Remove empty args which are not required
        // - Trim input args (not python functions)
        // - Update positions

        this.updatePositions()

        // TODO: really ugly code. fix this :-)
        let hints = JSON.parse(JSON.stringify(this.hints))
        for (let i = 0; i < hints.length; i++) {
          for (let j = 0; j < hints[i].statements.length; j++) {
            for (let hf = 0; hf < Object.keys(this.hintFunctions).length; hf++) {
              if (hints[i].statements[j].function_id === this.hintFunctions[Object.keys(this.hintFunctions)[hf]].id) {
                delete hints[i].statements[j].errors
                this.$delete(this.hints[i].statements[j], 'errors')

                if (!this.hintFunctions[Object.keys(this.hintFunctions)[hf]].args) {
                  continue
                }
                for (let a = 0; a < this.hintFunctions[Object.keys(this.hintFunctions)[hf]].args.length; a++) {
                  if ((hints[i].statements[j].args.length - 1) >= a) {
                    // Remove any errors
                    if ('error' in hints[i].statements[j].args[a]) {
                      this.$delete(this.hints[i].statements[j].args[a], 'error')
                      delete hints[i].statements[j].args[a].error
                    }
                    // Remove empty non-required args
                    if (!this.hintFunctions[Object.keys(this.hintFunctions)[hf]].args[a].required && !hints[i].statements[j].args[a].arg.length) {
                      hints[i].statements[j].args.splice(a, 1)
                      continue
                    }
                    // Split list-type args into multiple args (if it's not empty)
                    if (this.hintFunctions[Object.keys(this.hintFunctions)[hf]].args[a].type === 'list' && hints[i].statements[j].args[a].arg.length) {
                      let split = this.splitArgs(hints[i].statements[j].args[a].arg)
                      hints[i].statements[j].args.pop()
                      for (let k = 0; k < Object.keys(split).length; k++) {
                        hints[i].statements[j].args.push(split[Object.keys(split)[k]])
                      }
                    }
                    // Trim input args that aren't python
                    if (this.hintFunctions[Object.keys(this.hintFunctions)[hf]].group !== 'python') {
                      hints[i].statements[j].args[a].arg = hints[i].statements[j].args[a].arg.trim()
                    }
                  } else if (this.hintFunctions[Object.keys(this.hintFunctions)[hf]].args[a].required) {
                    break
                  }
                }
              }
            }
          }
        }

        this.errors = []

        OHMRepository.saveHints(hints, this.id_site, this.id_link)
        .then(response => {
          if (!response.headers['content-type'].startsWith('application/json')) {
            Events.$emit('addNotification', {
              type: 'error',
              msg: 'Response was not in JSON format...',
              timeout: 5
            })
          } else if (response.data && response.data.type) {
            const type = response.data.type
            const msg = response.data.message || 'Saved'

            Events.$emit('addNotification', {
              type: type,
              msg: msg,
              timeout: 5
            })

            if (type === 'success') {
              this.madeChanges = false
            }

            if (!response.data.errors) {
              // No errors returned means there are no errors :-). Remove all errors from all statements
              // TODO: When we got our in-client validator set up, this will no longer be needed
              this.hints.forEach(hint => {
                if (hint.error) {
                  hint.error = false
                }
                hint.statements.forEach(statement => {
                  if (statement.error) {
                    statement.error = false
                  }
                  if (statement.args) {
                    statement.args.forEach(arg => {
                      if (arg.error) {
                        arg.error = false
                      }
                    })
                  }
                })
              })

            }
          } else {
            Events.$emit('addNotification', {
              type: 'error',
              msg: 'An error has occurred (1)',
              timeout: 5
            })
          }
        }).catch(error => {
          if (error.response && error.response.data) {
            const type = error.response.data.type || 'error'
            const msg = error.response.data.message || 'An error has occurred (2)'

            Events.$emit('addNotification', {
              type: type,
              msg: msg,
              timeout: 5
            })

            if (error.response.data.errors) {
              error.response.data.errors.forEach(error => {
                // TODO: support other error types
                switch (error.type) {
                  case 'invalid_function': { // yes, the scope blocks are intentional
                    const hint = this.hints.find(e => e.id === error['link_filter_id'])

                    hint.error = true

                    if (hint && hint.statements.length > error['statement_position']) {
                      this.$set(
                        hint.statements[error['statement_position']],
                        'errors',
                        ['Invalid function']
                      )
                    }
                    break
                  }
                  case 'incompatible_function': {
                    const hint = this.hints.find(e => e.id === error['link_filter_id'])

                    hint.error = true

                    if (hint && hint.statements.length > error['statement_position']) {
                      this.$set(
                        hint.statements[error['statement_position']],
                        'errors',
                        ['Incompatible function. Did the site\'s OCL version change recently?']
                      )
                    }
                    break
                  }
                  case 'arg_invalid': {
                    const hint = this.hints.find(e => e.id === error['link_filter_id'])

                    hint.error = true

                    if (hint && hint.statements.length > error['statement_position'] && hint.statements[error['statement_position']].args.length > error['arg_position']) {
                      this.$set(
                        hint.statements[error['statement_position']].args[error['arg_position']],
                        'error',
                        'Argument invalid'
                      )
                    }
                    break
                  }
                  default:
                    break
                }
              })
            }
          } else {
            Events.$emit('addNotification', {
              type: 'error',
              msg: 'An error has occurred (3)',
              timeout: 5
            })
          }
        }).then(() => {
          this.saving = false
        })
      } else {
        Events.$emit('addNotification', {
          type: 'warning',
          msg: 'No changes to save',
          timeout: 3
        })
      }
    },
    updatePositions() {
      let i = 0
      this.madeChanges = true // FIXME: this sets madeChanges=true on initial component load

      const pythonPositions = {
        'A': 0,
        'B': 0,
        'L': 0,
        'M': 0,
        'S': 0,
        'X': 0,
        'Y': 0,
        'G': 0
      }

      // FIXME: move logic to hint.js store
      this.selectedHint.statements.forEach(statement => {
        statement.position = i++
        if (this.hintFunctions.length) {
          if (this.hintFunctions[statement.function_id].group === 'python') {
            if (pythonPositions[statement.function_id] < 9) {
              pythonPositions[statement.function_id]++
            }
            statement.python_name = statement.function_id + '-' + pythonPositions[statement.function_id]
          }
        }
      })
    },
  },
  computed: {
    ...mapState({
      id_site: state => state.id_site,
      id_link: state => state.id_link,
    }),
    ...mapState('site', {
      site: state => state.data
    }),
    ...mapState('article', {
      articles: state => state.latestArticles,
      selectedArticle: state => state.selected,
    }),
    ...mapState('hint', {
      hintFunctions: state => state.hintFunctions,
      selectedHint: state => state.selected,
    }),
    ...mapState('editor', {
      focused: state => state.focused,
      tabPage: state => state.tabPage,
    }),
  }
}
</script>

<style lang="sass">
@use '../styles/variables' as v

.ghost
  border-top: 2px dashed v.$light-gray3
  border-bottom: 2px dashed v.$light-gray3

.modal
  color: v.$light-black

.editor
  display: grid
  grid-template-rows: auto 1fr auto
  color: v.$white
  grid-area: editor
  background-color: v.$ui-background

  &.saving
    .editor-content
      user-select: none

      & > *
        opacity: 0.5
        pointer-events: none
        cursor: default

  &.minimized
    height: auto !important

  .links
    margin-left: auto

    li:last-child a
      padding-right: 16px

  .top-bar
    border-bottom: 1px solid v.$gray2
    display: flex
    flex-wrap: wrap

  .bottom-bar
    border-top: 1px solid v.$gray2
    display: flex
    align-items: center

    .site-info
      padding: 0 8px

      .ocl-version
        font-size: v.$font-size-small

  .toggle-btn
    margin-right: 10px

    &.focused-on
      background: v.$white
      color: v.$black

  .hint-list,
  .links
    display: flex
    flex-wrap: wrap

    li
      margin-bottom: -1px

  .ohm-modal
    .function-name
      white-space: nowrap

    .function-description
      white-space: pre-line

    .arg
      text-decoration: underline
      text-decoration-style: dotted
      cursor: help

    .obsolete
      color: v.$gray
</style>
